import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTrustedCircleApi,
  updateTrustedCircleApi,
  resendTrustedCircleInvitationApi,
  updateTrustedCircleMemberApi,
  updateTrustedCircleMemberRoleApi,
  sendBulkRemindersApi,
  addTrustedCircleMemberApi,
} from '../api/trustedCircle.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { mapRoleToApi, reminderToastMessage } from '../utils/trustedCircleFilters';
import PageHeader from '../components/PageHeader';

function getInitials(name) {
  return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function StatusBadge({ status }) {
  const styles = {
    Active: { background: '#DCFCE7', color: '#15803D' },
    Pending: { background: '#FEF3C7', color: '#B45309' },
    Inactive: { background: '#F1F5F9', color: '#64748B' },
  };
  const s = styles[status] || styles.Inactive;
  return (
    <span style={{ ...s, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
      {status}
    </span>
  );
}

function InviteBadge({ invite }) {
  const styles = {
    Accepted: { background: '#EFF6FF', color: '#1D4ED8' },
    Pending: { background: '#FEF3C7', color: '#B45309' },
    Sent: { background: '#F5F3FF', color: '#7E22CE' },
    Invited: { background: '#F5F3FF', color: '#7E22CE' },
  };
  const s = styles[invite] || { background: '#F1F5F9', color: '#64748B' };
  return (
    <span style={{ ...s, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99 }}>{invite}</span>
  );
}

export default function TrustedCircleDetail() {
  const navigate = useNavigate();
  const { circleId } = useParams();
  const [circleData, setCircleData] = useState(null);
  const [members, setMembers] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: 'Member', relation: 'Family' });
  const [toast, setToast] = useState(null);

  async function fetchTrustedCircle() {
    setLoading(true);

    const result = await apiHandler(() =>
      getTrustedCircleApi(circleId)
    );

    if (result.success) {
      const response = unwrapData(result);

      setCircleData(response?.circle || {});
      setMembers(response?.members || []);
      setMetrics(response?.metrics || {});
      setNotes(response?.circle?.notes || '');
    } else {
      showToast(result.message || 'Failed to fetch trusted circle', 'error');
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchTrustedCircle();
  }, [circleId]);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  async function handleResendInvite() {
    if (selectedIdx === null) {
      showToast('Select a member first', 'info');
      return;
    }

    const member = members[selectedIdx];

    const result = await apiHandler(() =>
      resendTrustedCircleInvitationApi(circleId, member.id)
    );

    if (result.success) {
      showToast(`Invite resent to ${member.name}`, 'success');
      fetchTrustedCircle();
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleChangeRole() {
    if (selectedIdx === null) {
      showToast('Select a member first', 'info');
      return;
    }

    const member = members[selectedIdx];

    if (member.role === 'Primary') {
      showToast('Cannot change Primary role', 'info');
      return;
    }

    const nextUiRole = member.role === 'Admin' ? 'Member' : 'Admin';
    const newRole = mapRoleToApi(nextUiRole);

    const result = await apiHandler(() =>
      updateTrustedCircleMemberRoleApi(circleId, member.id, { role: newRole })
    );

    if (result.success) {
      showToast(`Role updated to ${nextUiRole}`, 'success');
      fetchTrustedCircle();
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleChangeRoleAlternative() {
  if (selectedIdx === null) {
    showToast('Select a member first', 'info');
    return;
  }

  const member = members[selectedIdx];

  if (member.role === 'Primary') {
    showToast('Cannot change Primary role', 'info');
    return;
  }

  const newRole =
    member.role === 'Member'
      ? 'co-owner'
      : 'member';

  const result = await apiHandler(() =>
    updateTrustedCircleMemberRoleApi(
      circleId,
      member.id,
      {
        role: newRole,
      }
    )
  );

  if (result.success) {
    showToast(`Role updated successfully`, 'success');
    fetchTrustedCircle();
  } else {
    showToast(result.message, 'error');
  }
}

async function handleDeactivate() {
  if (selectedIdx === null) {
    showToast('Select a member first', 'info');
    return;
  }
  const m = members[selectedIdx];
  if (m.role === 'Primary') {
    showToast('Cannot deactivate Primary member', 'info');
    return;
  }
  const nextActive = m.status !== 'Active';

  const result = await apiHandler(() =>
    updateTrustedCircleMemberApi(circleId, m.id, { activeFlag: nextActive })
  );

  if (result.success) {
    showToast(`${m.name} ${nextActive ? 'reactivated' : 'deactivated'}`, 'success');
    fetchTrustedCircle();
  } else {
    showToast(result.message || 'Update failed', 'error');
  }
}

async function handleSaveNotes() {
  const result = await apiHandler(() =>
    updateTrustedCircleApi(circleId, { notes: notes.trim() })
  );
  if (result.success) {
    showToast('Circle notes saved', 'success');
    fetchTrustedCircle();
  } else {
    showToast(result.message || 'Failed to save notes', 'error');
  }
}

async function handleAddMember() {
  if (!newMember.name.trim()) {
    showToast('Enter member name', 'info');
    return;
  }
  const result = await apiHandler(() =>
    addTrustedCircleMemberApi(circleId, {
      invitedName: newMember.name.trim(),
      relationshipType: newMember.relation,
      role: mapRoleToApi(newMember.role),
    })
  );
  if (result.success) {
    setNewMember({ name: '', role: 'Member', relation: 'Family' });
    setShowAddModal(false);
    showToast(`${newMember.name} added & invite sent`, 'success');
    fetchTrustedCircle();
  } else {
    showToast(result.message || 'Failed to add member', 'error');
  }
}

async function handleBulkReminder() {
  const result = await apiHandler(() => sendBulkRemindersApi(circleId));
  if (result.success) {
    const data = unwrapData(result);
    showToast(reminderToastMessage(data), data?.sentCount > 0 ? 'success' : 'info');
  } else {
    showToast(result.message || 'Reminder failed', 'error');
  }
}

return (
  <>

    <PageHeader
      title="Circle Detail"
      icon="fa-circle-nodes"
      trail={[
        { label: 'Trusted Circles', to: '/trusted-circles' },
        { label: circleData?.circleId || 'N/A' },
      ]}
      actions={
        <>
          <button
            type="button"
            className="action-btn"
            style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
            onClick={() => navigate('/trusted-circles')}
          >
            <i className="fas fa-arrow-left" style={{ fontSize: 12 }} />
            Back
          </button>
          <button type="button" className="action-btn btn-orange" onClick={handleBulkReminder}>
            <i className="fas fa-bell" style={{ fontSize: 12 }} />
            Send Reminder
          </button>
          <button type="button" className="action-btn btn-green" onClick={() => setShowAddModal(true)}>
            <i className="fas fa-user-plus" style={{ fontSize: 12 }} />
            Add Member
          </button>
        </>
      }
    />

    {loading ? (
      <p style={{ padding: 24, color: '#64748B' }}>Loading circle…</p>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }} className="fade-up delay-1">

        {/* LEFT: Circle Summary */}
        <div className="section-card" style={{ padding: '22px 22px 26px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-circle-nodes" style={{ fontSize: 13, color: '#074f78' }} />
            </div>
            Circle summary
          </div>

          <div style={{ display: 'grid', gap: 16 }}>
            {[
              { label: 'Circle ID', value: circleData?.circleId, mono: true },
              { label: 'Primary user', value: circleData?.primaryUser },
              { label: 'Created', value: circleData?.created },
              { label: 'Status', value: circleData?.status },
              { label: 'State', value: circleData?.state },
            ].map(({ label, value, mono }) => (
              <div key={label}>
                <div className="profile-field-label">{label}</div>
                <div className="profile-field-readonly" style={mono ? { fontFamily: "'DM Mono',monospace", fontSize: 12, color: '#074f78', fontWeight: 700 } : {}}>
                  {label === 'Status' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16A34A', display: 'inline-block' }} />
                      <span style={{ color: '#15803D', fontWeight: 700 }}>{value}</span>
                    </span>
                  ) : value}
                </div>
              </div>
            ))}
          </div>

          {/* Mini stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 22, paddingTop: 18, borderTop: '1px solid #F1F5F9' }}>
            {[
              { label: 'Total members', value: metrics?.totalMembers || 0, color: '#074f78' },
              { label: 'Accepted', value: metrics?.acceptedMembers || 0, color: '#15803D' },
              { label: 'Pending', value: metrics?.pendingMembers || 0, color: '#B45309' },
              { label: 'Active', value: metrics?.activeMembers || 0, color: '#1D4ED8' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#FAFBFD', borderRadius: 10, padding: '10px 12px', border: '1px solid #F1F5F9' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: "'Roboto Slab',serif" }}>{value}</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: Members Table */}
        <div className="section-card" style={{ overflow: 'hidden', padding: 0 }}>
          <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-users" style={{ fontSize: 13, color: '#7E22CE' }} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Members</div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>
                  {metrics?.totalMembers || 0} member{metrics?.totalMembers !== 1 ? 's' : ''} · {metrics?.acceptedMembers || 0} accepted
                </div>
              </div>
            </div>
            {selectedIdx !== null && (
              <div style={{ fontSize: 12, color: '#64748B', background: '#F8FAFC', padding: '5px 12px', borderRadius: 8, border: '1px solid #E2E8F0' }}>
                Selected: <strong style={{ color: '#0F172A' }}>{members[selectedIdx]?.name}</strong>
              </div>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invite</th>
                  <th>Relation</th>
                  <th>Last action</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: '#94A3B8', fontSize: 13, padding: 20 }}>
                      No members in this circle yet.
                    </td>
                  </tr>
                ) : (
                  members.map((m, i) => (
                    <tr
                      key={i}
                      className="user-row"
                      onClick={() => setSelectedIdx(prev => prev === i ? null : i)}
                      style={{
                        cursor: 'pointer',
                        background: selectedIdx === i ? 'linear-gradient(90deg,rgba(7,79,120,.06) 0%,rgba(7,79,120,.01) 100%)' : undefined,
                        borderLeft: selectedIdx === i ? '3px solid #074f78' : '3px solid transparent',
                      }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: m.role === 'Primary' ? 'linear-gradient(135deg,#ec7426,#D9A441)' : 'linear-gradient(135deg,#102A43,#074f78)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                            {getInitials(m.name)}
                          </div>
                          <span style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>{m.name}</span>
                        </div>
                      </td>
                      <td>
                        {m.role === 'Primary'
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#FFF7ED,#FFFBEB)', color: '#C2410C', fontWeight: 700, fontSize: 11, padding: '3px 9px', borderRadius: 99, border: '1px solid #FED7AA' }}>
                            <i className="fas fa-star" style={{ fontSize: 9 }} />Primary
                          </span>
                          : <span style={{ background: m.role === 'Admin' ? '#EFF6FF' : '#F1F5F9', color: m.role === 'Admin' ? '#1D4ED8' : '#475569', fontWeight: 600, fontSize: 11, padding: '3px 9px', borderRadius: 99 }}>{m.role}</span>
                        }
                      </td>
                      <td><StatusBadge status={m.status} /></td>
                      <td><InviteBadge invite={m.invite} /></td>
                      <td style={{ color: '#64748B', fontSize: 12, fontWeight: 500 }}>{m.relation}</td>
                      <td style={{ color: m.lastAction === 'Today' ? '#15803D' : '#94A3B8', fontSize: 12, fontWeight: m.lastAction === 'Today' ? 600 : 400 }}>
                        {m.lastAction === 'Today' && <i className="fas fa-circle" style={{ fontSize: 6, marginRight: 4 }} />}
                        {m.lastAction}
                      </td>
                    </tr>
                  )))}
              </tbody>
            </table>
          </div>

          {/* Circle Notes */}
          <div style={{ padding: '16px 22px', borderTop: '1px solid #F1F5F9' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8 }}>Circle notes</div>
            <textarea
              className="staff-notes-textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ minHeight: 64 }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ padding: '14px 22px 20px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="action-btn btn-orange" onClick={handleResendInvite}>
              <i className="fas fa-paper-plane" style={{ fontSize: 12 }} />Resend Invite
            </button>
            <button className="action-btn btn-blue" onClick={handleChangeRole}>
              <i className="fas fa-user-gear" style={{ fontSize: 12 }} />Change Role
            </button>
            <button className="action-btn" style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }} onClick={handleDeactivate}>
              <i className="fas fa-ban" style={{ fontSize: 12 }} />Deactivate
            </button>
            <button type="button" className="action-btn btn-green" onClick={handleSaveNotes}>
              <i className="fas fa-floppy-disk" style={{ fontSize: 12 }} />Save Notes
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Add Member Modal */}
    {showAddModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 18, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
              <i className="fas fa-user-plus" style={{ color: '#ec7426', marginRight: 8 }} />Add Member
            </div>
            <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94A3B8' }}>
              <i className="fas fa-xmark" />
            </button>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            <div>
              <div className="profile-field-label">Full name</div>
              <input className="profile-field-input" placeholder="Enter member name" value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <div className="profile-field-label">Role</div>
              <select className="profile-field-input" value={newMember.role} onChange={e => setNewMember(p => ({ ...p, role: e.target.value }))}>
                <option>Member</option>
                <option>Admin</option>
              </select>
            </div>
            <div>
              <div className="profile-field-label">Relation</div>
              <select className="profile-field-input" value={newMember.relation} onChange={e => setNewMember(p => ({ ...p, relation: e.target.value }))}>
                {['Family', 'Partner', 'Sibling', 'Parent', 'Friend', 'Colleague', 'Other'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button className="action-btn" style={{ flex: 1, justifyContent: 'center', background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }} onClick={() => setShowAddModal(false)}>
              Cancel
            </button>
            <button className="action-btn btn-green" style={{ flex: 1, justifyContent: 'center' }} onClick={handleAddMember}>
              <i className="fas fa-user-plus" style={{ fontSize: 12 }} />Add & Send Invite
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Toast */}
    {toast && <div className={`cm-toast show ${toast.type}`}>{toast.msg}</div>}
  </>
);
}
